/**
 * space/rarity.js — how a prize shows off (GDD 9.2 "Prize effects").
 *
 * The GDD names four tiers and the effect each one gets, but not which prize
 * earns which tier. That judgement lives here, in one place, so the answer is
 * the same wherever it is asked.
 *
 * @module space/rarity
 */

import * as THREE from 'three';
import { SHELF_THEMES, STALL_BY_ID } from '../constants.js';

/** @param {string} id @returns {object} */
export function shelfTheme(id) {
  return SHELF_THEMES.find((t) => t.id === id) || SHELF_THEMES[0];
}

/**
 * Which show-off tier a plush sits in (GDD 9.2 "Prize effects").
 *
 * The GDD lists the four tiers without saying which prize earns which, so:
 * a large plush outranks a small one, and a prize from a 5-ticket stall
 * outranks the same prize from a 3-ticket stall.
 *
 * @param {{kind: string, stallId: string|null}} prize
 * @returns {'common'|'uncommon'|'rare'|'legendary'}
 */
export function plushRarity(prize) {
  const large = String(prize.kind || '').includes('large');
  const tier = STALL_BY_ID[prize.stallId]?.tier;
  const hard = tier === 'hard' || tier === 'premium';
  if (large) return hard ? 'legendary' : 'rare';
  return hard ? 'uncommon' : 'common';
}

/** A soft radial dot, reused for every halo and sparkle. */
let _sparkTexture = null;
export function sparkTexture() {
  if (_sparkTexture) return _sparkTexture;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,235,170,0.75)');
  g.addColorStop(1, 'rgba(255,200,80,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  _sparkTexture = new THREE.CanvasTexture(canvas);
  _sparkTexture.colorSpace = THREE.SRGBColorSpace;
  return _sparkTexture;
}

export function glowSprite(scale, color) {
  const m = new THREE.SpriteMaterial({
    map: sparkTexture(), transparent: true, blending: THREE.AdditiveBlending,
    depthWrite: false, color,
  });
  const s = new THREE.Sprite(m);
  s.scale.setScalar(scale);
  return s;
}

/** @param {THREE.Color} c @returns {number} Perceived brightness, 0–1. */
export function luminance(c) {
  return c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
}

/** Deterministic hue nudge so two stalls' plushes are visibly different. */
export function stallTint(stallId) {
  let h = 0;
  for (let i = 0; i < String(stallId).length; i++) h = (h * 31 + String(stallId).charCodeAt(i)) >>> 0;
  return (h % 360) / 360;
}
